/**
 * Vendor Invitation Service
 * Core business logic for vendor self-service onboarding
 */

import {
  VendorInvitation,
  VendorInviteRequest,
  VendorInviteResponse,
  VendorOnboardingData,
  PendingVendorStats,
  VendorInvitationFilters
} from '../../types/vendorInvitation';
import { db } from '../firebase/client';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  serverTimestamp,
  collectionGroup
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { twilioService } from '../sms/twilioService';

import { v4 as uuidv4 } from 'uuid';

class VendorInvitationService {
  private readonly COLLECTION = 'vendor_invitations';
  private readonly VENDORS_COLLECTION = 'vendors';
  private readonly EXPIRATION_HOURS = 48;

  /**
   * Create and send vendor invitation
   */
  async createInvitation(
    request: VendorInviteRequest,
    invitedBy: { id: string; name: string; email: string },
    company: { id: string; name: string }
  ): Promise<VendorInviteResponse> {
    try {


      // Validate phone number
      const phoneValidation = twilioService.validatePhoneNumber(request.phoneNumber);
      if (!phoneValidation.valid) {
        return {
          success: false,
          message: phoneValidation.error || 'Invalid phone number',
          error: phoneValidation.error,
        };
      }

      // Check if invitation already exists for this phone
      const existingInvitation = await this.findActiveInvitationByPhone(
        phoneValidation.formatted!,
        company.id
      );

      if (existingInvitation) {
        return {
          success: false,
          message: 'An active invitation already exists for this phone number',
          error: 'DUPLICATE_INVITATION',
        };
      }

      // Generate token and magic link
      const token = uuidv4();
      const baseUrl = window.location.origin;
      const magicLinkUrl = `${baseUrl}/vendor/onboard/${token}`;

      // Create invitation object
      const now = new Date();
      const expiresAt = new Date(now.getTime() + this.EXPIRATION_HOURS * 60 * 60 * 1000);

      const invitation: VendorInvitation = {
        id: uuidv4(),
        companyId: company.id, // Company identifier
        companyName: company.name,
        invitedBy: invitedBy.id,
        invitedByName: invitedBy.name,
        invitedByEmail: invitedBy.email,
        phoneNumber: phoneValidation.formatted!,
        email: request.email,
        contactName: request.contactName,
        companyNameSuggestion: request.companyName,
        token,
        magicLinkUrl,
        status: 'sent',
        sentAt: now,
        expiresAt,
        smsAttempts: 0,
        createdAt: now,
        updatedAt: now,
        metadata: {
          notes: request.notes,
          services: request.services,
        },
      };

      // Save to Firestore
      const invitationRef = doc(db, `companies/${company.id}/${this.COLLECTION}`, invitation.id);
      await setDoc(invitationRef, this.toFirestore(invitation));

      // Also create a flat lookup document for easy webhook access
      // This avoids collectionGroup query issues
      const phoneDigits = phoneValidation.formatted!.replace(/\D/g, '');
      const lookupRef = doc(db, 'vendor_invitation_lookups', phoneDigits);
      await setDoc(lookupRef, {
        invitationId: invitation.id,
        companyId: company.id,
        phoneNumber: phoneValidation.formatted!,
        status: invitation.status,
        createdAt: invitation.createdAt,
        updatedAt: invitation.updatedAt,
      });

      // Send SMS via Cloud Function (backend handles Twilio)
      try {
        const functions = getFunctions();
        const sendSMS = httpsCallable(functions, 'sendVendorInvitationSMS');

        const smsResult = await sendSMS({
          phoneNumber: phoneValidation.formatted!,
          companyName: company.name,
          contactName: request.contactName,
          invitedByName: invitedBy.name,
          magicLinkUrl,
        });

        // Update invitation with SMS result
        const smsData = smsResult.data as { success: boolean; messageSid?: string; status?: string };

        const updateData: Partial<VendorInvitation> = {
          smsAttempts: 1,
          updatedAt: new Date(),
          twilioMessageSid: smsData.messageSid,
          twilioStatus: smsData.status,
          status: smsData.success ? 'delivered' : 'sent',
          deliveredAt: smsData.success ? new Date() : undefined,
        };

        await updateDoc(
          doc(db, `companies/${company.id}/${this.COLLECTION}`, invitation.id),
          this.toFirestore(updateData)
        );

        return {
          success: true,
          message: 'Invitation sent successfully',
          invitationId: invitation.id,
        };
      } catch (smsError: any) {
        // SMS failed, but invitation was created
        console.error('❌ Failed to send SMS:', smsError);

        const updateData: Partial<VendorInvitation> = {
          smsAttempts: 1,
          updatedAt: new Date(),
          lastSMSError: smsError.message || 'SMS sending failed',
        };

        await updateDoc(
          doc(db, `companies/${company.id}/${this.COLLECTION}`, invitation.id),
          this.toFirestore(updateData)
        );

        return {
          success: false,
          message: 'Invitation created but SMS failed to send',
          error: smsError.message || 'SMS service error',
          invitationId: invitation.id,
        };
      }
    } catch (error) {
      console.error('❌ Error creating invitation:', error);
      return {
        success: false,
        message: 'Failed to create invitation',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Process SMS reply (YES/NO)
   */
  async processReply(
    phoneNumber: string,
    messageBody: string
  ): Promise<{ success: boolean; action: string }> {
    try {
      const phoneValidation = twilioService.validatePhoneNumber(phoneNumber);
      if (!phoneValidation.valid || !phoneValidation.formatted) {
        return { success: false, action: 'invalid_phone' };
      }
      const formatted = phoneValidation.formatted;
      const replyType = twilioService.parseReply(messageBody);


      // Find active invitation - this needs to be a cross-tenant query since we only have the phone number
      const invitation = await this.findActiveInvitationByPhone(formatted);

      if (!invitation) {
        // Send help message
        await twilioService.sendHelpMessage(formatted);
        return { success: false, action: 'no_invitation_found' };
      }

      switch (replyType) {
        case 'accept':
          return await this.handleAcceptance(invitation);

        case 'decline':
          return await this.handleDecline(invitation);

        case 'help':
          await twilioService.sendHelpMessage(formatted);
          return { success: true, action: 'help_sent' };

        case 'unknown':
          await twilioService.sendUnknownReplyMessage(formatted);
          return { success: true, action: 'unknown_reply' };
      }
    } catch (error) {
      console.error('❌ Error processing reply:', error);
      return { success: false, action: 'error' };
    }
  }

  /**
   * Handle acceptance reply
   */
  private async handleAcceptance(invitation: VendorInvitation): Promise<{ success: boolean; action: string }> {
    try {
      const invitationRef = doc(db, `companies/${invitation.companyId}/${this.COLLECTION}`, invitation.id);
      await updateDoc(invitationRef, {
        status: 'accepted',
        respondedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Send magic link
      const smsResult = await twilioService.sendMagicLink(invitation);

      if (smsResult.success) {
        await updateDoc(invitationRef, {
          smsAttempts: invitation.smsAttempts + 1,
          twilioMessageSid: smsResult.messageSid,
          updatedAt: serverTimestamp(),
        });
      }

      return { success: true, action: 'accepted' };
    } catch (error) {
      console.error('❌ Error handling acceptance:', error);
      return { success: false, action: 'error' };
    }
  }

  /**
   * Handle decline reply
   */
  private async handleDecline(invitation: VendorInvitation): Promise<{ success: boolean; action: string }> {
    try {
      const invitationRef = doc(db, `companies/${invitation.companyId}/${this.COLLECTION}`, invitation.id);
      await updateDoc(invitationRef, {
        status: 'declined',
        respondedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true, action: 'declined' };
    } catch (error) {
      console.error('❌ Error handling decline:', error);
      return { success: false, action: 'error' };
    }
  }

  /**
   * Complete vendor onboarding (after form submission)
   */
  async completeOnboarding(
    token: string,
    vendorData: VendorOnboardingData,
    authUserId?: string
  ): Promise<{ success: boolean; message: string; vendorId?: string }> {
    try {
      // Find invitation by token
      const invitation = await this.getInvitationByToken(token);

      if (!invitation) {
        return {
          success: false,
          message: 'Invalid or expired invitation link',
        };
      }

      // Check if already completed
      if (invitation.status === 'completed') {
        return {
          success: false,
          message: 'This invitation has already been completed',
        };
      }

      // Check expiration
      if (new Date() > invitation.expiresAt) {
        await this.markAsExpired(invitation.id, invitation.companyId);
        return {
          success: false,
          message: 'This invitation has expired',
        };
      }

      // Create vendor record
      const vendorId = uuidv4();
      const vendorRecord = {
        id: vendorId,
        companyId: invitation.companyId,

        // Basic info
        name: vendorData.companyName,
        dbaName: vendorData.dbaName,
        contactName: vendorData.contactName,
        contactTitle: vendorData.title,

        // Contact
        phone: vendorData.phoneNumber,
        alternatePhone: vendorData.alternatePhone,
        email: vendorData.email,
        website: vendorData.website,

        // Address
        address: vendorData.physicalAddress,
        mailingAddress: vendorData.sameAsPhysical
          ? vendorData.physicalAddress
          : vendorData.mailingAddress,

        // Services
        services: vendorData.servicesOffered,
        otherServices: vendorData.otherServiceDescription,
        serviceAreaRadius: vendorData.serviceAreaRadius,
        serviceAreaZipCodes: vendorData.serviceAreaZipCodes,

        // Availability
        offers247: vendorData.offers24_7Emergency,
        emergencyPhone: vendorData.emergencyPhone,
        responseTime: vendorData.responseTimeSLA,
        businessHours: vendorData.businessHours,

        // Financial
        paymentTerms: vendorData.paymentTerms,
        preferredPaymentMethod: vendorData.preferredPaymentMethod,
        taxId: vendorData.taxId,

        // Insurance
        insurance: {
          generalLiability: vendorData.generalLiabilityInsurance,
          workersComp: vendorData.workersCompInsurance,
        },

        // Licenses
        licenses: vendorData.licenses,

        // Safety
        osha30: vendorData.osha30Certified,
        certifications: vendorData.otherCertifications,

        // References
        references: vendorData.references,

        // Metadata
        onboardedVia: 'self-service',
        onboardedBy: invitation.invitedBy,
        invitationId: invitation.id,
        profileCompleteness: this.calculateProfileCompleteness(vendorData),

        // Status
        status: 'pending_approval', // Requires manager approval
        active: false, // Not active until approved
        userId: authUserId || null, // Link to Auth User

        // Audit
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Save vendor
      await setDoc(
        doc(db, `companies/${invitation.companyId}/${this.VENDORS_COLLECTION}`, vendorId),
        vendorRecord
      );

      // Update invitation
      const invitationRef = doc(db, `companies/${invitation.companyId}/${this.COLLECTION}`, invitation.id);
      await updateDoc(invitationRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        vendorId,
        vendorData: this.toFirestore(vendorData),
        updatedAt: serverTimestamp(),
      });

      // Send confirmation SMS
      await twilioService.sendConfirmation(invitation);

      return {
        success: true,
        message: 'Profile submitted successfully',
        vendorId,
      };
    } catch (error) {
      console.error('❌ Error completing onboarding:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get invitation by token
   */
  async getInvitationByToken(token: string): Promise<VendorInvitation | null> {

    try {
      // This query is cross-tenant, which is a security risk.
      // However, the token is a UUIDv4, making it unguessable.
      // A compromised token would only expose a single invitation.
      // For enhanced security, this could be moved to a Cloud Function
      // that verifies the tenant based on the domain context of the request.

      const q = query(
        collectionGroup(db, this.COLLECTION),
        where('token', '==', token)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const docData = snapshot.docs[0].data();

      // If multiple found (unlikely), take the first one
      return this.fromFirestore(docData);
    } catch (error) {
      console.error('❌ Error finding invitation by token:', error);
      // Check if it's a missing index error
      if (error instanceof Error && error.message.includes('index')) {
        console.error('🚨 INDEX ERROR: The collectionGroup index for token may be missing. Check Firebase Console.');
      }
      return null;
    }
  }

  /**
   * Get invitation by ID
   */
  async getInvitationById(id: string, companyId: string): Promise<VendorInvitation | null> {
    try {
      const docRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.fromFirestore(docSnap.data());
    } catch (error) {
      console.error('Error getting invitation:', error);
      return null;
    }
  }

  /**
   * Get all invitations for a company
   */
  async getInvitationsByCompany(
    companyId: string,
    filters?: VendorInvitationFilters
  ): Promise<VendorInvitation[]> {
    try {
      let q = query(
        collection(db, `companies/${companyId}/${this.COLLECTION}`),
        orderBy('createdAt', 'desc')
      );

      if (filters?.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        q = query(q, where('status', 'in', statuses));
      }

      if (filters?.invitedBy) {
        q = query(q, where('invitedBy', '==', filters.invitedBy));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.fromFirestore(doc.data()));
    } catch (error) {
      console.error('Error getting invitations:', error);
      return [];
    }
  }

  /**
   * Get pending vendor statistics
   */
  async getStats(companyId: string): Promise<PendingVendorStats> {
    try {
      const invitations = await this.getInvitationsByCompany(companyId);

      return {
        total: invitations.length,
        sent: invitations.filter(i => i.status === 'sent' || i.status === 'delivered').length,
        accepted: invitations.filter(i => i.status === 'accepted').length,
        completed: invitations.filter(i => i.status === 'completed').length,
        expired: invitations.filter(i => i.status === 'expired').length,
        declined: invitations.filter(i => i.status === 'declined').length,
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return { total: 0, sent: 0, accepted: 0, completed: 0, expired: 0, declined: 0 };
    }
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitationId: string, companyId: string): Promise<VendorInviteResponse> {
    try {
      const invitation = await this.getInvitationById(invitationId, companyId);

      if (!invitation) {
        return {
          success: false,
          message: 'Invitation not found',
        };
      }

      // Reset expiration
      const now = new Date();
      const newExpiresAt = new Date(now.getTime() + this.EXPIRATION_HOURS * 60 * 60 * 1000);

      // Send SMS via Cloud Function
      const functions = getFunctions();
      const sendSMS = httpsCallable(functions, 'sendVendorInvitationSMS');

      try {
        const result = await sendSMS({
          phoneNumber: invitation.phoneNumber,
          companyName: invitation.companyName,
          magicLinkUrl: invitation.magicLinkUrl,
        });

        const smsData = result.data as { success: boolean; messageSid?: string; status?: string; error?: string };

        if (!smsData.success) {
          throw new Error(smsData.error || 'SMS failed');
        }

        // Update invitation
        const invitationRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, invitationId);
        await updateDoc(invitationRef, {
          status: 'delivered',
          expiresAt: Timestamp.fromDate(newExpiresAt),
          smsAttempts: invitation.smsAttempts + 1,
          twilioMessageSid: smsData.messageSid,
          lastSMSError: null,
          updatedAt: serverTimestamp(),
        });

        return {
          success: true,
          message: 'Invitation resent successfully',
        };
      } catch (smsError: any) {
        const errorMsg = smsError.message || 'Failed to send SMS';

        // Update invitation with error
        const invitationRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, invitationId);
        await updateDoc(invitationRef, {
          lastSMSError: errorMsg,
          updatedAt: serverTimestamp(),
        });

        return {
          success: false,
          message: 'Failed to send SMS',
          error: errorMsg,
        };
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      return {
        success: false,
        message: 'Failed to resend invitation',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitationId: string, companyId: string): Promise<{ success: boolean; message: string }> {
    try {
      const invitationRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, invitationId);
      await updateDoc(invitationRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        success: true,
        message: 'Invitation cancelled',
      };
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      return {
        success: false,
        message: 'Failed to cancel invitation',
      };
    }
  }

  // Helper methods

  private async findActiveInvitationByPhone(
    phoneNumber: string,
    companyId?: string
  ): Promise<VendorInvitation | null> {
    try {
      const activeStatuses = ['sent', 'delivered', 'accepted'];

      let q;
      if (companyId) {
        // Query within a specific company
        q = query(
          collection(db, `companies/${companyId}/${this.COLLECTION}`),
          where('phoneNumber', '==', phoneNumber),
          where('status', 'in', activeStatuses)
        );
      } else {
        // Cross-tenant query for webhook processing
        q = query(
          collectionGroup(db, this.COLLECTION),
          where('phoneNumber', '==', phoneNumber),
          where('status', 'in', activeStatuses)
        );
      }

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      // Return the most recent one if multiple exist
      const invitations = snapshot.docs
        .map(doc => this.fromFirestore(doc.data()))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      return invitations[0];

    } catch (error) {
      console.error('Error finding active invitation by phone:', error);
      return null;
    }
  }

  private async markAsExpired(invitationId: string, companyId: string): Promise<void> {
    const invitationRef = doc(db, `companies/${companyId}/${this.COLLECTION}`, invitationId);
    await updateDoc(invitationRef, {
      status: 'expired',
      updatedAt: serverTimestamp(),
    });
  }

  private calculateProfileCompleteness(data: VendorOnboardingData): number {
    let score = 0;
    const fields = [
      data.companyName, // Required
      data.contactName, // Required
      data.phoneNumber, // Required
      data.email, // Required
      data.physicalAddress?.street,
      data.servicesOffered?.length > 0,
      data.paymentTerms,
      data.website,
      data.taxId,
      data.generalLiabilityInsurance,
      data.licenses?.length,
      data.references?.length,
    ];

    fields.forEach(field => {
      if (field) score += 100 / fields.length;
    });

    return Math.round(score);
  }

  private toFirestore(data: any): any {
    const converted: any = { ...data };

    // Convert Date objects to Timestamps
    Object.keys(converted).forEach(key => {
      if (converted[key] instanceof Date) {
        converted[key] = Timestamp.fromDate(converted[key]);
      }
    });

    return converted;
  }

  private fromFirestore(data: any): any {
    const converted: any = { ...data };

    // Convert Timestamps to Dates
    Object.keys(converted).forEach(key => {
      if (converted[key] instanceof Timestamp) {
        converted[key] = converted[key].toDate();
      }
    });

    return converted;
  }
}

export const vendorInvitationService = new VendorInvitationService();
